/**
 * The dashboard's visual grammar for a site's state, in one place so the KPI
 * row, the badges, and the sparklines can never disagree with each other.
 *
 * Threshold logic lives in the backend (`nova.monitoring.checks`); this module
 * only maps the state it is given onto design-system tones and tokens.
 */

import type { DayUptime, SiteStatus } from "@/lib/api";

export type SiteState = SiteStatus["status"];

/**
 * URL search param carrying which site's detail drawer is open (`?site=<id>`),
 * shared by the heat rows, the site table and the drawer itself so every
 * "door" on the board opens the same L2 view (dashboard design rules §4.2).
 */
export const SITE_DETAIL_PARAM = "site";

/**
 * URL search param carrying the board's site filter (`?sites=id1,id2`) — the
 * one filter row scopes the heat rows, the latency trend and the site table.
 */
export const SITE_FILTER_PARAM = "sites";

/** Tone shared by `Badge`, `Statistic`, `Meter` and `MiniSparkline` — their unions overlap here. */
export type StatusTone = "success" | "warning" | "danger" | "neutral";

export const STATUS_LABEL: Record<SiteState, string> = {
  up: "Operational",
  degraded: "Degraded",
  down: "Down",
  unknown: "Awaiting check",
};

export const STATUS_TONE: Record<SiteState, StatusTone> = {
  up: "success",
  degraded: "warning",
  down: "danger",
  unknown: "neutral",
};

/** Segment colours for the estate composition bar. */
export const STATUS_COLOR: Record<SiteState, string> = {
  up: "var(--norican-success)",
  degraded: "var(--norican-warning)",
  down: "var(--norican-danger)",
  unknown: "var(--norican-bg-muted)",
};

/** Order used wherever states are listed, worst last. */
export const STATE_ORDER: readonly SiteState[] = ["up", "degraded", "down", "unknown"];

/* ── Daily-rollup grammar (shared by the heat rows and the site drawer) ── */

/** A day at or above this uptime reads as healthy. */
export const HEALTHY_PCT = 99.5;
/** A day between this and {@link HEALTHY_PCT} reads as shaky. */
export const SHAKY_PCT = 95;

/** `StatusStrip.Cell` status for one day's rollup. */
export type CellStatus = "positive" | "caution" | "negative" | "neutral";

/** Tint for a day's cell — no recorded checks stays neutral, never green. */
export function dayStatus(day: DayUptime): CellStatus {
  if (!day.checks || day.uptime_pct == null) {
    return "neutral";
  }
  if (day.uptime_pct >= HEALTHY_PCT) {
    return "positive";
  }
  if (day.uptime_pct >= SHAKY_PCT) {
    return "caution";
  }
  return "negative";
}

/** Format a rollup day (`YYYY-MM-DD`) as a short local date. */
export function formatDay(iso: string): string {
  const at = new Date(`${iso}T00:00:00Z`);
  // `toLocaleDateString` on an unparseable date returns the literal string
  // "Invalid Date", which then goes straight onto the board — and into whatever
  // screenshot ends up in a pack. An em dash says the same thing without
  // pretending it is a date.
  if (Number.isNaN(at.getTime())) {
    return "—";
  }
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Hover title for one day's cell — says "no checks" rather than implying health. */
export function dayTitle(day: DayUptime): string {
  if (!day.checks || day.uptime_pct == null) {
    return `${formatDay(day.day)} — no checks recorded`;
  }
  const checks = `${day.checks} ${day.checks === 1 ? "check" : "checks"}`;
  return `${formatDay(day.day)} — ${day.uptime_pct.toFixed(2)}% uptime over ${checks}`;
}

/** Meter/badge tone for a window-uptime percentage, same thresholds as the cells. */
export function uptimeTone(pct: number | null | undefined): StatusTone {
  if (pct == null) {
    return "neutral";
  }
  if (pct >= HEALTHY_PCT) {
    return "success";
  }
  return pct >= SHAKY_PCT ? "warning" : "danger";
}

export function formatMs(ms: number | null | undefined): string {
  if (ms == null) {
    return "—";
  }
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)} s` : `${Math.round(ms)} ms`;
}

export function formatPct(pct: number | null | undefined): string {
  if (pct == null) {
    return "—";
  }
  return `${pct.toFixed(1)}%`;
}

/** Strip the scheme (and any trailing slash) so a table cell reads as a hostname. */
export function displayHost(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

/* ── Redirect drift (the registered URL vs the one that answered) ── */

export interface RedirectDrift {
  /** The URL that actually answered the last probe. */
  finalUrl: string;
  /** How many hops the probe followed to get there. */
  hops: number;
  /** Whether the hops changed the scheme, e.g. `http://` → `https://`. */
  schemeChanged: boolean;
}

function scheme(url: string): string {
  return url.split(":", 1)[0] ?? "";
}

/**
 * Where the last probe landed, when that is not where it was aimed.
 *
 * `null` covers the two non-drift states, which are not the same as each other:
 * `redirect_count === 0` (the registered URL answered directly) and
 * `redirect_count == null` (nothing answered, so nothing is known).
 *
 * NOVA keeps probing the registered URL regardless — changing what is monitored
 * is the target list owner's call, so this only surfaces the discrepancy.
 */
export function redirectDrift(site: SiteStatus): RedirectDrift | null {
  if (!site.redirect_count || !site.final_url || site.final_url === site.url) {
    return null;
  }
  return {
    finalUrl: site.final_url,
    hops: site.redirect_count,
    schemeChanged: scheme(site.url) !== scheme(site.final_url),
  };
}

/** Hover text for a drifted target — names what is, and isn't, being measured. */
export function driftTitle(drift: RedirectDrift): string {
  const hops = `${drift.hops} ${drift.hops === 1 ? "redirect" : "redirects"}`;
  const measured = drift.schemeChanged
    ? "NOVA probes the registered URL, so an HTTPS or certificate failure at this address would not show here."
    : "NOVA probes the registered URL, not this one.";
  return `Answered at ${drift.finalUrl} after ${hops}. ${measured}`;
}
